import React, { useState } from 'react';
import { ICreatePasswordEntry, IDecryptedPasswordEntry } from '../../types/vault.types';
import Modal from '../common/Modal';
import PasswordForm from './PasswordForm';
import { useVault } from '../../contexts/VaultContext';
import { toast } from 'react-hot-toast';
import { XMarkIcon, KeyIcon } from '@heroicons/react/24/outline';

interface PasswordModalProps {
    isOpen: boolean;
    onClose: () => void;
    entry?: IDecryptedPasswordEntry;
    onSuccess: () => void;
    categoryId?: string;
}

const PasswordModal: React.FC<PasswordModalProps> = ({
    isOpen,
    onClose,
    entry,
    onSuccess,
    categoryId
}) => {
    const { addEntry } = useVault();
    const [loading, setLoading] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    const handleClose = () => {
        if (hasChanges) {
            if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
                onClose();
            }
        } else {
            onClose();
        }
    };

    const handleSubmit = async (data: ICreatePasswordEntry) => {
        try {
            setLoading(true);

            if (!data.title || !data.username || !data.password) {
                throw new Error('Please fill in all required fields');
            }

            await addEntry(data);
            setHasChanges(false);
            if (onSuccess) {
                await onSuccess();
            }
            onClose();
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to add password';
            console.error('Failed to add password:', error);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Enhanced Backdrop */}
            <div className="fixed inset-0 bg-gradient-to-br from-black/60 to-black/40 backdrop-blur-sm transition-all duration-300"></div>

            {/* Modal Container */}
            <div className="flex min-h-full items-center justify-center p-4">
                <div className="relative bg-white rounded-3xl w-full max-w-7xl mx-4 max-h-[95vh] overflow-hidden shadow-2xl border border-gray-100">
                    {/* Modal Header */}
                    <div className="sticky top-0 z-10 bg-gradient-to-r from-primary-600 to-primary-700 text-white px-8 py-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm">
                                    <KeyIcon className="w-7 h-7 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-bold">
                                        Add New Password
                                    </h3>
                                    <p className="text-primary-100 text-sm">
                                        Create a new entry for your password
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-3 text-white/80 hover:text-white hover:bg-white/20 rounded-2xl transition-all duration-200"
                            >
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        </div>
                    </div>

                    {/* Modal Content */}
                    <div className="flex-1 overflow-auto max-h-[calc(95vh-140px)] bg-gray-50">
                        <div className="p-8">
                            <PasswordForm
                                initialData={entry}
                                onSubmit={handleSubmit}
                                onCancel={handleClose}
                                isLoading={loading}
                                categoryId={categoryId}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PasswordModal;